// ============================================================================
// usePlanStore — der Plan, die Auswahl, die Geschichte und die Sicherung
// (SPEC §5.2). Und sonst nichts.
//
// Die SZENE steht hier ABSICHTLICH nicht: sie wird in `Editor.tsx` mit
// `useMemo` gebaut (§5.7.3). Eine Szene im Speicher wäre eine zweite Wahrheit
// über denselben Plan und müsste bei jedem Zug mitgepflegt werden.
//
// Was die Schale (fe-shell) aus diesem Speicher benutzt, ist VERTRAG und darf
// sich nicht ändern (frontend/README.md §5.2):
//   planId, plan?.title, undoTiefe, redoTiefe, zurueck(), vor(),
//   aendern(rezept, opt), metaAendern(patch)
//
// `aendern` nimmt ein Rezept (`PlanRezept`), das den Plan an Ort und Stelle
// ändert ODER einen NEUEN Plan zurückgibt. Beides wird gebraucht: der
// Inspektor schreibt in den Plan, `TopNav` ersetzt ihn beim Öffnen einer
// JSON-Datei (`aendern(() => neuerPlan, { normalisieren: true })`, §5.8.2).
// Darum `(p: Plan) => Plan | void` und nicht nur `=> void` wie im Codeblock
// von §5.2 — der Aufruf in §5.8.2 verlangt die Rückgabe.
//
// ---------------------------------------------------------------------------
// DIE FÜNF HARTEN REGELN DER AUTOSICHERUNG (§5.2.2, Anhang A.12). Sie sind der
// Grund, warum dieser Speicher keine Bibliothek benutzt:
//
//   1. KEIN PUT ohne echten Unterschied zum letzten BESTÄTIGTEN Serverstand.
//      Öffnen, Blättern, Zoomen und Exportieren erzeugen keine Version.
//   2. 409 hält die Wiederholung AN. Ein zweiter Versuch mit nachgezogener
//      Version hebelte die optimistische Sperre aus — die Version kommt NIE
//      aus einem 409.
//   3. 401 setzt `anonym` (über `wegweiser`) und lässt den Entwurf STEHEN.
//      Sonst zeichnet jemand nach 12 h weiter in einem Editor, der nichts mehr
//      sichert und nicht zur Anmeldung führt (R-20).
//   4. Status 0 oder >= 500: erneut in 10 s. JEDE andere 4xx beendet die
//      Schleife — eine zu große Nutzlast ginge sonst für immer alle 10 s hinaus.
//   5. Ein Lauf für einen Plan, der nicht mehr offen ist, wird verworfen.
// ============================================================================
import { create } from "zustand";
import { klon, maxMi, normalisiere, TB, uid } from "@kern";
import type { Eintrag, FerienQuelle, Marquee, Mi, Plan } from "@kern";
import { holePlan, legePlanAn, speicherePlan } from "../api/client";
import type { PlanSpeichern } from "../api/client";
import type { Person, PlanVoll } from "../api/typen";
import { entwurfLesen, entwurfLoeschen, entwurfSchreiben, zoomLesen, zoomSchreiben } from "../lib/entwurf";
import { ApiError } from "../lib/fetchJson";
import { TX } from "../lib/texte";
import { toast } from "./toasts";
import { wegweiser } from "./useAuthStore";

export type Speicherstand = "leer" | "gespeichert" | "ungesichert" | "speichert" | "fehler" | "konflikt";

/** Der Rumpf eines 409 — überall dieselben Schlüssel (Anhang A.2). */
export interface Konflikt {
  aktuelle_version: number;
  geaendert_von: Person | null;
  geaendert_am: string;
}

/**
 * Ein Rezept ändert den Plan an Ort und Stelle ODER gibt einen NEUEN zurück.
 * Beides wird gebraucht: der Inspektor schreibt hinein, `TopNav` ersetzt ihn
 * beim Öffnen einer JSON-Datei (§5.8.2). Der `void`-Zweig ist Pflicht — ohne
 * ihn wäre ein `(p) => { p.title = x }` (Rückgabetyp `void`) nicht zuweisbar.
 */
export type PlanRezept = (p: Plan) => Plan | void;

export interface AendernOpt {
  /** Koaleszenzmarke. Gleiche Marke + offenes Fenster = KEIN neuer Undo-Eintrag. */
  marke?: string;
  /** Fenster in ms; ohne Angabe bleibt die Marke offen, bis markeSchliessen() laeuft. */
  fenster_ms?: number;
  /** false = kein Undo-Eintrag (Regler waehrend oninput). Standard true. */
  historie?: boolean;
  /** true = normalisiere() nach der Aenderung (Zeitraum, Spuren, Import). Standard false. */
  normalisieren?: boolean;
}

interface PlanZustand {
  // --- Zustand
  planId: string | null;
  plan: Plan | null;
  version: number;
  meta: { titel: string; kunde: string; projekt_nr: string; schlagworte: string[] } | null;
  /** T48: "claude", solange der Plan ein Claude-Entwurf ist. */
  entwurfVon: "claude" | null;
  auswahl: string | null;
  marquee: Marquee | null;
  /** 0,25…3 — REIN visuell (T25). Steht nie im Plan. */
  zoom: number;
  ferien: FerienQuelle | null;
  speicherstand: Speicherstand;
  /** "10:41" — die Uhrzeit des letzten bestätigten PUT. */
  gespeichertUm: string | null;
  konflikt: Konflikt | null;
  undoTiefe: number;
  redoTiefe: number;
  /** Balken mit offener Inline-Eingabe auf dem Blatt. */
  inlineId: string | null;

  // --- Leben des Plans
  planSetzen(voll: PlanVoll, ferien?: FerienQuelle | null): void;
  planSchliessen(): void;
  ferienSetzen(q: FerienQuelle | null): void;

  // --- Aendern
  aendern(rezept: PlanRezept, o?: AendernOpt): void;
  markeSchliessen(): void;
  metaAendern(patch: Partial<NonNullable<PlanZustand["meta"]>>): void;
  /** Nach POST /plaene/{id}/entwurf-uebernehmen (T48). */
  entwurfUebernommen(): void;

  // --- Auswahl und Bedienung
  waehlen(id: string | null): void;
  marqueeSetzen(m: Marquee | null): void;
  zoomSetzen(z: number): void;
  inlineOeffnen(id: string | null): void;
  /** Gibt die id des neuen Eintrags zurück. */
  /** `hoehe` und `drehung` nur beim Aufziehen über mehrere Zeilen (T42). */
  eintragAnlegen(lane: string, row: number, a: Mi, b: Mi, hoehe?: number, drehung?: 0 | 90): string;
  eintragLoeschen(id: string): void;
  eintragDuplizieren(id: string): void;

  // --- Geschichte
  zurueck(): void;
  vor(): void;

  // --- Sicherung
  jetztSichern(): Promise<boolean>;
  serverstandLaden(): Promise<void>;
  /**
   * Legt den lokalen Stand als NEUEN Plan an und gibt dessen Kennung zurück.
   *
   * ABWEICHUNG vom Codeblock in §5.2, dort `Promise<void>`: §5.2.2 verlangt
   * ausdrücklich, dass der Speicher NICHT selbst navigiert und "die Route der
   * Aufrufer über den Rückgabewert bekommt". Mit `void` wäre das unmöglich.
   * Aufrufer ist nur `KonfliktBanner` (dieselbe Dateimenge).
   */
  alsKopieSpeichern(): Promise<string | null>;
  konfliktSchliessen(): void;
}

// ---------------------------------------------------------------------------
// Innereien. Sie stehen ABSICHTLICH nicht im Zustand: ein Undo-Stapel im
// Speicher würde bei jedem Zug jede Komponente neu zeichnen, die den Speicher
// liest. Nach außen sichtbar sind nur `undoTiefe`/`redoTiefe`.
// ---------------------------------------------------------------------------
interface Innereien {
  /** JSON-Zeichenketten ganzer Pläne, Tiefe 80 (§5.2.1). */
  undo: string[];
  redo: string[];
  letzteMarke: string | null;
  letzteZeit: number;
  /** JSON des letzten BESTÄTIGTEN Serverstands — gegen ihn prüft Regel 1. */
  letzterServerstand: string | null;
  /** dasselbe für die vier Listenfelder; sie gehen im selben PUT mit. */
  letzteMeta: string | null;
  /** Der schwebende Entprellungs-Zeitgeber der Autosicherung (1,5 s). */
  timer: number | null;
  /** Der Wiederholungs-Zeitgeber nach Status 0/5xx (10 s). */
  wieder: number | null;
  /** Der laufende PUT, damit `jetztSichern()` ihn abwarten kann. */
  aktuell: Promise<boolean> | null;
  /** Während eines PUT kam eine weitere Änderung (Regel 8). */
  nochmal: boolean;
  /** 409 oder eine andere 4xx: die Schleife ist aus, bis der Mensch handelt. */
  gestoppt: boolean;
}

const UNDO_TIEFE = 80;
/** Entprellung der Autosicherung (E4/T6). */
const NACH_MS = 1500;
/** Zweiter Versuch nach einem Netz- oder Serverfehler (§5.2.2 Regel 5). */
const WIEDER_MS = 10000;

const H: Innereien = {
  undo: [],
  redo: [],
  letzteMarke: null,
  letzteZeit: 0,
  letzterServerstand: null,
  letzteMeta: null,
  timer: null,
  wieder: null,
  aktuell: null,
  nochmal: false,
  gestoppt: false,
};

function timerAus(): void {
  if (H.timer !== null) {
    window.clearTimeout(H.timer);
    H.timer = null;
  }
  if (H.wieder !== null) {
    window.clearTimeout(H.wieder);
    H.wieder = null;
  }
}

function innereienLeeren(): void {
  H.undo = [];
  H.redo = [];
  H.letzteMarke = null;
  H.letzteZeit = 0;
  H.letzterServerstand = null;
  H.letzteMeta = null;
  H.nochmal = false;
  H.gestoppt = false;
  timerAus();
}

/**
 * §5.2.1: Ein Eintrag entsteht, wenn `historie !== false` UND (keine Marke
 * ODER eine andere Marke ODER das Fenster ist zu). Ohne Fensterangabe bleibt
 * die Marke offen, bis `markeSchliessen()` läuft — das ist der Zug mit der
 * Maus: EIN Zug, EIN Eintrag.
 */
function legtEintragAn(o: AendernOpt | undefined): boolean {
  if (o?.historie === false) return false;
  if (!o?.marke) return true;
  if (o.marke !== H.letzteMarke) return true;
  return o.fenster_ms !== undefined && Date.now() - H.letzteZeit > o.fenster_ms;
}

/** Vorzustand auf den Undo-Stapel, Redo verwerfen, Tiefe deckeln. */
function geschichtePushen(planJson: string): void {
  H.undo.push(planJson);
  if (H.undo.length > UNDO_TIEFE) H.undo.shift();
  H.redo.length = 0;
}

/**
 * Das Ergebnis eines Rezepts auswerten: ein zurückgegebener Plan gewinnt,
 * sonst gilt der geänderte Klon.
 *
 * Die Prüfung ist ein `typeof`-Wächter und keine Wahrheitsprüfung: ein Wert
 * vom Typ `void` darf in TypeScript nicht auf Wahrheit geprüft werden, und
 * genau diesen Typ hat ein Rezept, das nur hineinschreibt.
 */
function ergebnisPlan(ergebnis: Plan | void, ersatz: Plan): Plan {
  return typeof ergebnis === "object" && ergebnis !== null ? ergebnis : ersatz;
}

/** Bleibt die Auswahl im Zielzustand gültig? Sonst `null` (§5.2.1). */
function auswahlHalten(plan: Plan, auswahl: string | null): string | null {
  if (!auswahl) return null;
  return plan.items.some((i) => i.id === auswahl) ? auswahl : null;
}

const uhrzeit = () =>
  new Date().toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });

/** Eine Person aus einem fremden Rumpf lesen, ohne ihm zu glauben (I3). */
function personAus(rohes: unknown): Person | null {
  if (!rohes || typeof rohes !== "object") return null;
  const o = rohes as Record<string, unknown>;
  if (typeof o.id !== "string") return null;
  return { id: o.id, anzeigename: typeof o.anzeigename === "string" ? o.anzeigename : null };
}

/**
 * Der Konfliktrumpf eines 409 (Anhang A.2). Er wird GEPRÜFT und nicht
 * geglaubt: das Banner zeigt die Zahl an, und eine fehlende Zahl darf keine
 * „Version NaN" auf den Schirm schreiben.
 */
function konfliktAus(e: unknown, ersatz: number): Konflikt {
  const rohes = e instanceof ApiError ? e.body : null;
  if (rohes && typeof rohes === "object") {
    const o = rohes as Record<string, unknown>;
    return {
      aktuelle_version: typeof o.aktuelle_version === "number" ? o.aktuelle_version : ersatz,
      geaendert_von: personAus(o.geaendert_von),
      geaendert_am: typeof o.geaendert_am === "string" ? o.geaendert_am : "",
    };
  }
  return { aktuelle_version: ersatz, geaendert_von: null, geaendert_am: "" };
}

// ---------------------------------------------------------------------------

export const usePlanStore = create<PlanZustand>((set, get) => {
  /**
   * Der eigentliche PUT. Gibt `true` zurück, wenn nach diesem Lauf nichts
   * mehr offen ist — nur darauf verlässt sich der Navigationswächter.
   */
  async function laufIntern(): Promise<boolean> {
    const { planId, plan, version, meta, speicherstand } = get();
    if (!planId || !plan) return true;
    if (H.gestoppt) return false;

    const json = JSON.stringify(plan);
    const metaJson = JSON.stringify(meta);

    // Regel 1: kein PUT ohne echten Unterschied. Ein Undo, das genau zum
    // Serverstand zurückführt, gilt damit als gesichert — und nicht als
    // ewig offene Änderung.
    if (json === H.letzterServerstand && metaJson === H.letzteMeta) {
      if (speicherstand === "ungesichert" || speicherstand === "speichert") {
        set({ speicherstand: "gespeichert" });
      }
      return true;
    }

    H.nochmal = false;
    set({ speicherstand: "speichert" });

    const rumpf: PlanSpeichern = { version, daten: plan };
    if (meta) {
      rumpf.titel = meta.titel;
      rumpf.kunde = meta.kunde;
      rumpf.projekt_nr = meta.projekt_nr;
      rumpf.schlagworte = meta.schlagworte;
    }

    try {
      const antwort = await speicherePlan(planId, rumpf);
      // Regel 5: der Plan ist nicht mehr offen — die Antwort gehört niemandem.
      if (get().planId !== planId) return false;

      H.letzterServerstand = json;
      H.letzteMeta = metaJson;

      const jetzt = get().plan;
      const offen = H.nochmal || (jetzt !== null && JSON.stringify(jetzt) !== json);

      // Regel 10: der Entwurf ist erledigt — aber nur, wenn wirklich nichts
      // mehr offen ist. Kam während des PUT eine Änderung, wird er mit der
      // NEUEN Serverversion neu geschrieben; mit der alten würde §5.2.3 ihn
      // beim nächsten Öffnen als veraltet verwerfen.
      if (offen && jetzt) entwurfSchreiben(planId, antwort.version, jetzt);
      else entwurfLoeschen(planId);

      set({
        version: antwort.version,
        gespeichertUm: uhrzeit(),
        speicherstand: offen ? "ungesichert" : "gespeichert",
        konflikt: null,
      });

      // Regel 8: die bestätigte Version ist nachgezogen, jetzt darf der
      // nächste Lauf geplant werden — sonst läuft er in ein selbst erzeugtes 409.
      if (offen) {
        H.nochmal = false;
        planen();
        return false;
      }
      return true;
    } catch (e: unknown) {
      if (get().planId !== planId) return false;
      const status = e instanceof ApiError ? e.status : 0;

      // Regel 2: 409 hält an. Die Version aus dem Rumpf wird ANGEZEIGT,
      // nie übernommen.
      if (status === 409) {
        H.gestoppt = true;
        set({ speicherstand: "konflikt", konflikt: konfliktAus(e, version) });
        return false;
      }

      // Regel 3: 401 — Schleife aus, Entwurf STEHEN LASSEN, zur Anmeldung.
      if (status === 401) {
        H.gestoppt = true;
        set({ speicherstand: "fehler" });
        wegweiser(e);
        toast.warn(TX.sicherung.sitzungWeg);
        return false;
      }

      // Regel 4a: Netz weg oder Server kaputt — in 10 s erneut.
      if (status === 0 || status >= 500) {
        set({ speicherstand: "fehler" });
        if (H.wieder !== null) window.clearTimeout(H.wieder);
        H.wieder = window.setTimeout(() => {
          H.wieder = null;
          void lauf();
        }, WIEDER_MS);
        return false;
      }

      // Regel 4b: jede andere 4xx liegt am Rumpf und heilt nicht durch Warten.
      H.gestoppt = true;
      set({ speicherstand: "fehler" });
      toast.error(e instanceof Error ? e.message : TX.sicherung.fehler);
      return false;
    }
  }

  /**
   * Einen Lauf starten oder sich an den laufenden hängen. `H.aktuell` ist der
   * einzige Riegel gegen zwei gleichzeitige PUT auf denselben Plan.
   */
  function lauf(): Promise<boolean> {
    if (H.aktuell) {
      H.nochmal = true;
      return H.aktuell;
    }
    const p = laufIntern();
    H.aktuell = p;
    const frei = () => {
      if (H.aktuell === p) H.aktuell = null;
    };
    void p.then(frei, frei);
    return p;
  }

  /** Den PUT mit 1,5 s Entprellung planen (§5.2.2 Regel 1). */
  function planen(): void {
    if (H.timer !== null) window.clearTimeout(H.timer);
    H.timer = window.setTimeout(() => {
      H.timer = null;
      void lauf();
    }, NACH_MS);
  }

  /** Entwurf ablegen und den PUT planen — nach JEDER Änderung. */
  function gemerkt(plan: Plan): void {
    const { planId, version } = get();
    if (planId) entwurfSchreiben(planId, version, plan);
    planen();
  }

  return {
    planId: null,
    plan: null,
    version: 0,
    meta: null,
    entwurfVon: null,
    auswahl: null,
    marquee: null,
    zoom: 1,
    ferien: null,
    speicherstand: "leer",
    gespeichertUm: null,
    konflikt: null,
    undoTiefe: 0,
    redoTiefe: 0,
    inlineId: null,

    planSetzen(voll, ferien) {
      innereienLeeren();
      H.letzterServerstand = JSON.stringify(voll.daten);
      const meta = {
        titel: voll.titel,
        kunde: voll.kunde,
        projekt_nr: voll.projekt_nr,
        schlagworte: voll.schlagworte,
      };
      H.letzteMeta = JSON.stringify(meta);

      let plan = voll.daten;
      let speicherstand: Speicherstand = "gespeichert";
      let offen = false;

      // §5.2.3: ein Entwurf dieses Geräts gilt nur, wenn er gegen DIESELBE
      // Serverversion entstanden ist und sich wirklich unterscheidet. Ist er
      // älter, ist der Server weiter — dann wird er verworfen, nicht angeboten.
      const entwurf = entwurfLesen(voll.id);
      if (entwurf) {
        if (entwurf.version === voll.version && JSON.stringify(entwurf.daten) !== H.letzterServerstand) {
          plan = entwurf.daten;
          speicherstand = "ungesichert";
          offen = true;
          toast.warn(TX.sicherung.entwurfZurueck, { dauer: 8000 });
        } else if (entwurf.version < voll.version) {
          entwurfLoeschen(voll.id);
          toast.info(TX.sicherung.entwurfVerworfen);
        }
      }

      set({
        planId: voll.id,
        plan,
        version: voll.version,
        meta,
        entwurfVon: voll.entwurf_von ?? null,
        auswahl: null,
        marquee: null,
        inlineId: null,
        // Der Zoom ist eine Eigenschaft des Arbeitsplatzes (T25): er kommt aus
        // dem localStorage und geht nie in den Plan.
        zoom: zoomLesen(voll.id) ?? 1,
        // `undefined` heißt "lass das Ferienpaket, wie es ist", `null` heißt
        // "es gibt keins". Mit `??` wäre beides dasselbe.
        ferien: ferien !== undefined ? ferien : get().ferien,
        speicherstand,
        gespeichertUm: null,
        konflikt: null,
        undoTiefe: 0,
        redoTiefe: 0,
      });

      // Ein wiederhergestellter Entwurf ist eine ungesicherte Änderung — er
      // gehört gesichert, sonst liegt er nach der zweiten Anmeldung wieder da.
      if (offen) planen();
    },

    entwurfUebernommen() {
      set({ entwurfVon: null });
    },

    planSchliessen() {
      innereienLeeren();
      set({
        planId: null,
        plan: null,
        version: 0,
        meta: null,
        entwurfVon: null,
        auswahl: null,
        marquee: null,
        inlineId: null,
        zoom: 1,
        speicherstand: "leer",
        gespeichertUm: null,
        konflikt: null,
        undoTiefe: 0,
        redoTiefe: 0,
      });
    },

    ferienSetzen(q) {
      set({ ferien: q });
    },

    aendern(rezept, o) {
      const { plan } = get();
      if (!plan) return;

      // Der Vorzustand wird VOR der Änderung abgelegt — und nur, wenn §5.2.1 es
      // sagt. Der Prototyp pusht bei jedem `pointerdown`; damit kostet ein Zug
      // über zwanzig Rahmen zwanzig Einträge.
      if (legtEintragAn(o)) geschichtePushen(JSON.stringify(plan));
      H.letzteMarke = o?.marke ?? null;
      H.letzteZeit = Date.now();

      // Der Speicher arbeitet auf KLONEN: `plan` ist nach jeder Änderung ein
      // neues Objekt, und genau darauf verlässt sich das `useMemo` der Szene in
      // Editor.tsx (§5.7.3) — Referenzgleichheit als Abhängigkeit.
      const kopie = klon(plan);
      const neu = ergebnisPlan(rezept(kopie), kopie);
      const fertig = o?.normalisieren ? normalisiere(neu) : neu;

      gemerkt(fertig);

      const auswahl = auswahlHalten(fertig, get().auswahl);
      set({
        plan: fertig,
        auswahl,
        // Eine Inline-Eingabe an einem Balken, den es nicht mehr gibt, staende
        // sonst als Textfeld ueber dem leeren Papier.
        inlineId: auswahlHalten(fertig, get().inlineId),
        speicherstand: get().speicherstand === "konflikt" ? "konflikt" : "ungesichert",
        undoTiefe: H.undo.length,
        redoTiefe: H.redo.length,
      });
    },

    markeSchliessen() {
      H.letzteMarke = null;
      H.letzteZeit = 0;
    },

    metaAendern(patch) {
      const { meta } = get();
      if (!meta) return;
      const neu = { ...meta, ...patch };
      if (JSON.stringify(neu) === JSON.stringify(meta)) return;
      set({
        meta: neu,
        speicherstand: get().speicherstand === "konflikt" ? "konflikt" : "ungesichert",
      });
      planen();
    },

    waehlen(id) {
      // Eine Auswahl schließt die Inline-Eingabe eines ANDEREN Balkens: sonst
      // schreibt man in einen Balken, der nicht mehr der gewählte ist.
      set({ auswahl: id, inlineId: get().inlineId === id ? id : null });
    },

    marqueeSetzen(m) {
      set({ marquee: m });
    },

    zoomSetzen(z) {
      const zoom = Math.min(3, Math.max(0.25, z));
      const { planId } = get();
      if (planId) zoomSchreiben(planId, zoom);
      set({ zoom });
    },

    inlineOeffnen(id) {
      set({ inlineId: id });
    },

    eintragAnlegen(lane, row, a, b, hoehe = 1, drehung = 0) {
      const plan = get().plan;
      if (!plan) return "";
      const spur = plan.lanes.find((l) => l.id === lane);
      const eintrag: Eintrag = {
        id: uid(),
        lane,
        row,
        a,
        b,
        text: TB.neuerEintrag,
        // Der Standardstil der SPUR, nicht "solid": eine Meilensteinspur legt
        // Meilensteine an (Prototyp `lane.def`).
        style: spur?.def ?? "solid",
        icon: "none",
        iconPos: "center",
        align: "center",
        color: "",
        size: 0,
        hoehe,
        drehung,
        iconColor: "",
        frei: false,
        dx: 0,
        dy: 0,
      };
      get().aendern((p) => {
        p.items.push(eintrag);
      });
      set({ auswahl: eintrag.id });
      return eintrag.id;
    },

    eintragLoeschen(id) {
      // Auswahl und Inline-Eingabe raeumt `aendern` selbst auf: beide werden
      // dort gegen die Eintragsliste des ZIELzustands geprueft.
      get().aendern((p) => {
        p.items = p.items.filter((i) => i.id !== id);
      });
    },

    eintragDuplizieren(id) {
      const plan = get().plan;
      if (!plan) return;
      const it = plan.items.find((i) => i.id === id);
      if (!it) return;
      // Wortgleich zum Prototyp (`duplicateSel`): die Kopie liegt HINTER dem
      // Vorbild und wird am Zeitraumende geklemmt, die Länge bleibt.
      const w = it.b - it.a;
      const hi = maxMi(plan);
      const kopie: Eintrag = { ...it, id: uid(), a: Math.min(it.b, hi - w), b: Math.min(it.b + w, hi) };
      get().aendern((p) => {
        p.items.push(kopie);
      });
      set({ auswahl: kopie.id });
    },

    zurueck() {
      const { plan, auswahl } = get();
      const vorher = H.undo.pop();
      if (!plan || vorher === undefined) return;
      H.redo.push(JSON.stringify(plan));
      const neu = JSON.parse(vorher) as Plan;
      // Nach einem Sprung in der Geschichte ist jede offene Marke zu: der
      // nächste Tastendruck darf nicht in einen Eintrag koaleszieren, der
      // gerade rückgängig gemacht wurde.
      H.letzteMarke = null;
      gemerkt(neu);
      set({
        plan: neu,
        auswahl: auswahlHalten(neu, auswahl),
        inlineId: null,
        undoTiefe: H.undo.length,
        redoTiefe: H.redo.length,
        speicherstand: get().speicherstand === "konflikt" ? "konflikt" : "ungesichert",
      });
    },

    vor() {
      const { plan, auswahl } = get();
      const spaeter = H.redo.pop();
      if (!plan || spaeter === undefined) return;
      H.undo.push(JSON.stringify(plan));
      const neu = JSON.parse(spaeter) as Plan;
      H.letzteMarke = null;
      gemerkt(neu);
      set({
        plan: neu,
        auswahl: auswahlHalten(neu, auswahl),
        inlineId: null,
        undoTiefe: H.undo.length,
        redoTiefe: H.redo.length,
        speicherstand: get().speicherstand === "konflikt" ? "konflikt" : "ungesichert",
      });
    },

    async jetztSichern() {
      // Regel 11: einen schwebenden Lauf SOFORT ausführen und abwarten.
      timerAus();
      const fertig = await lauf();
      if (fertig) return true;
      // Kam während des Laufs noch eine Änderung, ist genau EIN Nachschlag
      // fällig — nicht eine Schleife, die der Wächter nie verlässt.
      if (H.nochmal && !H.gestoppt) {
        H.nochmal = false;
        timerAus();
        return await lauf();
      }
      return false;
    },

    async serverstandLaden() {
      const { planId } = get();
      if (!planId) return;
      timerAus();
      const voll = await holePlan(planId);
      // Erst den Entwurf weg, DANN setzen: sonst stellt planSetzen genau den
      // Entwurf wieder her, den man gerade verwerfen wollte (§5.2.2).
      entwurfLoeschen(planId);
      get().planSetzen(voll);
      set({ gespeichertUm: uhrzeit() });
    },

    async alsKopieSpeichern() {
      const { plan, meta, planId } = get();
      if (!plan) return null;
      timerAus();
      try {
        const voll = await legePlanAn({
          titel: meta?.titel || plan.title,
          kunde: meta?.kunde ?? "",
          projekt_nr: meta?.projekt_nr ?? "",
          schlagworte: meta?.schlagworte ?? [],
          daten: plan,
        });
        // Der Entwurf des ALTEN Plans ist damit erledigt: sein Inhalt steckt
        // jetzt in der Kopie, und beim nächsten Öffnen des alten Plans wäre er
        // eine Wiederherstellung, die niemand mehr will.
        if (planId) entwurfLoeschen(planId);
        return voll.id;
      } catch (e: unknown) {
        if (wegweiser(e)) return null;
        toast.error(e instanceof Error ? e.message : TX.sicherung.fehler);
        return null;
      }
    },

    konfliktSchliessen() {
      // Der Hinweis geht weg, die Sperre bleibt: `H.gestoppt` wird hier NICHT
      // gelöst. Wer weiterzeichnet, sieht "Nicht gespeichert" — und die
      // Entscheidung (laden oder Kopie) steht weiter aus. Ein stiller zweiter
      // Versuch mit nachgezogener Version wäre das Ende der Sperre (Regel 2).
      set({ konflikt: null, speicherstand: "ungesichert" });
    },
  };
});

/**
 * `dirty` für den Navigationswächter (§5.2.4). Eigene Funktion und kein Feld:
 * sie wird aus einem Ref gelesen, nicht aus dem Renderstand — ein Klick auf
 * die Navigation läuft NACH dem `blur` des Feldes.
 */
export function ungesichert(): boolean {
  const s = usePlanStore.getState().speicherstand;
  return s === "ungesichert" || s === "speichert" || s === "konflikt";
}
